import React, { useCallback, useRef, useState } from "react";
import ReportTable from "components/Report";
import dayjs from "dayjs";
import customDateFormat from "dayjs/plugin/customParseFormat";
import {
	getUserFullName,
	stringToMask,
	toStringCurrency
} from "helpers/string";
import ls from "Localization";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
	Badge,
	Button,
	Card,
	CardBody,
	Col,
	Container,
	Modal,
	ModalBody,
	ModalHeader,
	Row
} from "reactstrap";
import { UncontrolledTooltip } from "reactstrap/lib";
import { FaRegFileExcel } from "react-icons/fa";
import { toast } from "react-toastify";
import { nfseStatus, NfseType } from "types/nfseStatus";
import { updateCompany } from "../Edit/actions";
import * as actions from "./actions";
import * as api from "services/api";
import NFSeDataEditForm from "containers/Company/components/NFSeDataEdit/NFSeDataEditForm";
import ExportExcelForm from "containers/Company/components/ExportExcelForm";
import ExportExcel from "helpers/exportExcel";
import { getCompanyById } from "../Detail/actions";
import { nfeBusinessStatus } from "types/NFeBusinessStatus";
import EyeIcon from "mdi-react/EyeIcon";
import ImportNfse from "containers/Company/components/ImportNfse";

dayjs.extend(customDateFormat);

let getNfseDebounced = null;

const buildFilterQuery = filtered =>
	filtered.reduce(
		(p, c) => p + `&filters[${c.id}]=${encodeURIComponent(c.value)}`,
		""
	);

const NfseInCompany = ({ history, companyId }) => {
	const dispatch = useDispatch();
	const table = useRef(null);

	const { items, pages, count, loading } = useSelector(
		state => state.companyNfseList
	);
	const { item } = useSelector(state => state.companyDetail);
	const { token } = useSelector(s => s.login);
	const { loading: loadingEdit, errors } = useSelector(s => s.companyEdit);
	const nfse_data_edit_form = useSelector(s => s.form.nfse_data_edit_form);

	const companyFilter = `&filters[${
		item?.isHired ? "clientMei" : "mei"
	}]=${companyId}`;

	const [modal, setModal] = useState(false);
	const toggle = () => {
		setModal(!modal);
	};

	const [modalExportExcel, setModalExportExcel] = useState(false);
	const [arrayFiltered, setArrayFiltered] = useState([]);
	const [progress, setProgress] = useState(0);

	const toggleModalExportExcel = () =>
		setModalExportExcel(prevState => !prevState);

	const [showNfEmissionPassword, setShowNfEmissionPassword] = useState(false);
	const toggleNfEmissionPassword = () =>
		setShowNfEmissionPassword(!showNfEmissionPassword);

	const handleFetchData = useCallback(
		tableState => {
			if (!tableState) return;
			let { page, pageSize, sorted, filtered, toExport, callback } = tableState;

			if (getNfseDebounced) {
				getNfseDebounced.cancel();
			}

			if (!sorted) sorted = [];

			let creationDate = filtered.find(c => c.id === "creationDate");

			if (creationDate && creationDate.value) {
				filtered = filtered.filter(c => c.id !== "creationDate");

				if (creationDate.value.startDate) {
					filtered.push({
						id: "startDate",
						value: creationDate.value.startDate
					});
				}

				if (creationDate.value.endDate)
					filtered.push({
						id: "endDate",
						value: creationDate.value.endDate
					});
			}

			setArrayFiltered(filtered);

			getNfseDebounced = _.debounce(
				() =>
					dispatch(
						actions.getNfses(
							page * pageSize,
							pageSize,
							companyFilter + buildFilterQuery(filtered),
							sorted[0] ? sorted[0].id : undefined,
							sorted[0] ? sorted[0].desc : undefined,
							toExport,
							callback
						)
					),
				500
			);
			getNfseDebounced();
		},
		[companyFilter]
	);

	const load = () => {
		handleFetchData(table.current ? table.current.state : undefined);
	};

	const maskDocument = doc =>
		doc?.length === 11
			? stringToMask(doc, "###.###.###-##")
			: stringToMask(doc, "##.###.###/####-##");

	const customData = data => {
		let rows = [];

		for (let nfse of data) {
			rows.push({
				"Data de Solicitação": nfse.createdAt
					? dayjs(nfse.createdAt).format("YYYY-MM-DD")
					: "",
				"Última Atualização": nfse.updatedAt
					? dayjs(nfse.updatedAt).format("YYYY-MM-DD")
					: "",
				Status: ls[nfseStatus[nfse.status]],
				"Tipo de solicitação": nfse.type ? ls[NfseType[nfse.type]] : "N/A",
				"Empresa prestadora": nfse.mei?.businessName || "",
				"Nome prestador": nfse.companyProvider?.providerUser?.name || "",
				"CNPJ Prestador": nfse.mei?.cnpj ? maskDocument(nfse.mei.cnpj) : "",
				"Cliente (tomador)":
					nfse.clientMei?.businessName || nfse.client?.name || "",
				"CNPJ Cliente":
					nfse.clientMei?.cnpj || nfse.client?.document
						? maskDocument(nfse.clientMei?.cnpj || nfse.client?.document)
						: "",
				"Email Cliente": nfse.contactCompany?.email || "",
				"Valor Total": nfse.totalValue
					? toStringCurrency(+nfse.totalValue / 100.0)
					: "",
				"Motivo do erro":
					nfse.statusReason && nfse.status === 9 ? nfse.statusReason : "",
				"Emitido por": nfse.user ? getUserFullName(nfse.user) : "",
				"Link Arquivo": nfse.file || ""
			});
		}

		return {
			columns: rows[0] ? Object.keys(rows[0]) : [],
			data: rows
		};
	};

	const handleExport = async types => {
		const dataTmp = [];
		const takeTmp = 500;

		const filterText = companyFilter + buildFilterQuery(arrayFiltered);

		try {
			for (let skipTmp = 0; skipTmp < count; skipTmp += takeTmp) {
				const response = await api.sendGet(
					`/nfse?skip=${skipTmp}&take=${takeTmp}&sort=createdAt&desc=true${filterText}`,
					{
						Authorization: "Bearer " + token
					}
				);

				const result = await response.json();

				if (!result.isValid) {
					throw new Error(
						`Erro ao buscar dados do relatório (skip ${skipTmp}, take ${takeTmp})`
					);
				}

				dataTmp.push(...result.model);
				setProgress(Math.round((skipTmp / count) * 100));
			}

			if (!dataTmp.length) {
				toast.info("Nenhuma nota fiscal encontrada para os filtros aplicados.");
				return;
			}

			const dataSet = customData(dataTmp);

			for (const type of types) {
				const success = ExportExcel(
					dataSet,
					`NFs_${item?.businessName || companyId}`,
					type
				);

				if (success) {
					toast.success(
						`Exportação (${type.toUpperCase()}) concluída: ${
							dataTmp.length
						} nota(s).`
					);
				} else {
					toast.error(
						`Não foi possível exportar as notas fiscais (${type.toUpperCase()}).`
					);
				}
			}
		} catch (error) {
			console.log(error);
			toast.error("Não foi possível buscar as notas fiscais para exportação.");
		} finally {
			setProgress(0);
			setModalExportExcel(false);
		}
	};

	const onSubmitExportExcel = data => {
		const types = [];
		if (data.exportExcel) types.push("xlsx");
		if (data.exportCsv) types.push("csv");

		if (!types.length) return;

		if (count > 500) {
			confirm(
				"Por favor, aguarde alguns instantes...\nEssa exportação pode demorar um pouco"
			);
		}

		handleExport(types);
	};

	const onSubmit = async data => {
		dispatch(
			updateCompany(
				companyId,
				{
					nfEmissionLogin: data?.nfEmissionLogin,
					nfEmissionPassword: data?.nfEmissionPassword,
					nfEmissionUrl: data?.nfEmissionUrl
				},
				error => {
					if (error) {
						console.log(error);
					} else {
						toggle();
						alert("Dados alterados com sucesso!");
						dispatch(getCompanyById(companyId));
					}
				}
			)
		);
	};

	return (
		<Container className="dashboard">
			{!item?.isHired && (
				<Row>
					<Col>
						<Card>
							<CardBody>
								<Row className="justify-content-between align-items-center">
									<div className="card__title">
										<h5 className="bold-text">Dados Nota Fiscal</h5>
									</div>
									<div>
										<Button color={"primary"} type={"submit"} onClick={toggle}>
											Editar
										</Button>
									</div>
								</Row>

								<Row>
									<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
										<p>
											<strong>Login: </strong>
											<br />
											<span>{item?.nfEmissionLogin || "--"}</span>
										</p>
									</Col>

									<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
										<strong>Senha: </strong>
										<div className="form__form-group-field">
											<input
												readOnly
												type={showNfEmissionPassword ? "text" : "password"}
												value={item?.nfEmissionPassword || "--"}
											/>
											<button
												className={`form__form-group-button${
													showNfEmissionPassword ? " active" : ""
												}`}
												onClick={toggleNfEmissionPassword}
												type="button"
											>
												<EyeIcon />
											</button>
										</div>
									</Col>

									<Col className="mt-3" xs={9} sm={9} md={4} xl={2}>
										<p>
											<strong>Status: </strong>
											<br />
											<Badge
												color={item?.nfeStatus ? "primary" : "secondary"}
												style={{
													fontSize: 12
												}}
											>
												{ls[nfeBusinessStatus[item?.nfeStatus]]}
											</Badge>
										</p>
									</Col>

									<Col className="mt-3" xs={12} sm={12} md={6} xl={3}>
										<p>
											<strong>URL: </strong>
											<br />
											<span>{item?.nfEmissionUrl || "--"}</span>
										</p>
									</Col>
								</Row>
							</CardBody>
						</Card>
					</Col>
				</Row>
			)}

			<Row>
				<Col xs={12}>
					<Card>
						<CardBody>
							<ReportTable
								manual
								tableRef={table}
								title={"Nota fiscal de serviço eletrônica"}
								count={count}
								data={items}
								pageSize={5}
								pages={pages}
								loading={loading.getAll}
								onFetchData={handleFetchData}
								filterable
								noExport
								defaultSorted={[
									{
										id: "createdAt",
										desc: true
									}
								]}
								headerRightComponent={
									<div className="d-flex">
										<ImportNfse
											id={companyId}
											load={() => load()}
											loading={loading?.import}
										/>
										<Button
											className={"ml-2"}
											outline
											onClick={toggleModalExportExcel}
											id="tooltipExcelCompanyNfse"
										>
											<FaRegFileExcel style={{ margin: 0 }} />
										</Button>
										<UncontrolledTooltip target="tooltipExcelCompanyNfse">
											Exportar os dados da tabela
										</UncontrolledTooltip>
									</div>
								}
								onRowClicked={row => history.push(`/nfses/${row._id}`)}
								columns={[
									{
										Header: () => (!item?.isHired ? "Tomador" : "Emissor"),
										id: !item?.isHired
											? "clientMei.businessName"
											: "mei.businessName",
										filterable: true,
										accessor: ({ clientMei, client, mei }) =>
											!item?.isHired
												? clientMei?.businessName || client?.name
												: mei?.businessName
									},
									{
										Header: () =>
											!item?.isHired ? "CNPJ Tomador" : "CNPJ Emissor",
										id: !item?.isHired ? "clientMei.cnpj" : "mei.cnpj",
										filterable: false,
										accessor: ({ clientMei, client, mei }) => {
											const c = !item?.isHired
												? clientMei?.cnpj || client?.document
												: mei?.cnpj;

											return c?.length === 11
												? stringToMask(c, "###.###.###-##")
												: stringToMask(c, "##.###.###/####-##");
										}
									},
									{
										Header: "Valor Total",
										id: "totalValue",
										filterable: true,
										accessor: c =>
											c.totalValue
												? toStringCurrency(+c.totalValue / 100.0)
												: "--"
									},
									{
										Header: "Observações",
										id: "observations",
										filterable: false,
										accessor: c => (c.observations ? c.observations : "--")
									},

									{
										Header: "Data Criação",
										id: "createdAt",
										accessor: c =>
											c.createdAt
												? dayjs(c.createdAt).format(ls.dateFormatWithoutSeconds)
												: "--"
									},
									{
										Header: "Última atualização",
										id: "updatedAt",
										accessor: c =>
											c.updatedAt
												? dayjs(c.updatedAt).format(ls.dateFormatWithoutSeconds)
												: "--"
									},
									{
										Header: "Emitido por",
										id: "user.name",
										accessor: ({ user }) => (user ? getUserFullName(user) : ""),
										width: 150
									},
									{
										Header: ls.status,
										id: "status",
										show: true,
										accessor: c => ls[nfseStatus[c.status]],
										Filter: ({ filter, onChange }) => {
											return (
												<select
													onChange={event => onChange(event.target.value)}
													style={{ width: "100%" }}
													value={filter && filter.value ? filter.value : "all"}
												>
													<option value="">Todos</option>
													{Object.keys(nfseStatus)
														.filter(c => !isNaN(+c))
														.map(c => (
															<option key={c} value={c}>
																{ls[nfseStatus[c]]}
															</option>
														))}
												</select>
											);
										},
										width: 140
									}
								]}
							/>
						</CardBody>
					</Card>
				</Col>
			</Row>

			<Modal isOpen={modal} toggle={toggle}>
				<ModalHeader toggle={toggle} className={"card__title bold-text"}>
					Nota Fiscal
				</ModalHeader>

				<ModalBody className="theme-light">
					<NFSeDataEditForm
						onSubmit={onSubmit}
						initialValues={item}
						loading={loadingEdit}
						isInvalid={nfse_data_edit_form && !!nfse_data_edit_form.syncErrors}
						errors={errors}
					/>
				</ModalBody>
			</Modal>

			<Modal isOpen={modalExportExcel} toggle={toggleModalExportExcel}>
				<ModalHeader
					toggle={toggleModalExportExcel}
					className={"card__title bold-text"}
				>
					Exportar Relatório
				</ModalHeader>

				<ModalBody className="theme-light">
					<ExportExcelForm onSubmit={onSubmitExportExcel} progress={progress} />
				</ModalBody>
			</Modal>
		</Container>
	);
};

export default NfseInCompany;
